import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const MakeReadyAndMaintenance = () => (
  <Page
    title="Make Ready and Maintenance"
    intro="Make Ready checks a vehicle or asset's load list against what is actually on board and keeps a full history of every check, while maintenance logs track servicing history for individual assets."
  >
    <Section title="The Make Ready module">
      <Bullets>
        <Bullet>
          <DocLink url="/make-ready-overview" text="Make Ready" /> is one
          module with three tabs: <strong>Board</strong> (fleet-wide status
          of every vehicle and asset), <strong>Load Lists</strong> (create
          and edit the standards each one is checked against — shown to
          Governance, Managers, Supervisors, and site administrators), and{' '}
          <strong>History</strong> (the audit trail and trend panels). The{' '}
          <strong>Restock Queue</strong> button sits in the module header.
        </Bullet>
        <Bullet>
          Load lists are managed from the Load Lists tab rather than Site
          Configuration — see{' '}
          <DocLink url="/fleet-equipment/load-lists" text="Load Lists" docsPage />
          . Old Site Configuration links redirect to the new location.
        </Bullet>
      </Bullets>
      <Info title="Subscription tier">
        Make Ready is a tier 2 feature and depends on configured load lists.
      </Info>
    </Section>

    <Section title="The Board">
      <Bullets>
        <Bullet>
          The Board breaks the fleet down into Expired, Missing Items,
          Expiring Soon, Never Checked, Good, and <strong>Untracked</strong>.
          Click a chip or a slice of the health ring to filter; search by
          call sign or asset ID and sort the cards.
        </Bullet>
        <Bullet>
          <strong>Untracked</strong> means the vehicle or asset has no load
          list, so it has never had a check and never appeared on the Board
          before. Untracked cards show a dashed grey border and a load list
          picker — the list most of its peers (same vehicle type, or same
          asset category and sub-category) use is pre-selected — with an{' '}
          <strong>Assign and run first check</strong> button that assigns
          the list and opens the check straight away. If no suitable list
          exists yet, the card offers to create one.
        </Bullet>
        <Bullet>
          Every tracked card has a <strong>Run check</strong> button on its
          face (<strong>Run first check</strong> for anything never checked)
          and, for Governance, Managers, Supervisors, and site
          administrators, an <strong>Edit load list</strong> shortcut.
          Expand a card to see exactly what is expired, missing, or expiring
          soon.
        </Bullet>
        <Bullet>
          The Needs Attention feed alongside lists individual flagged items
          in urgency order, and starts with a row counting anything
          untracked — its <strong>Assign</strong> action filters the Board
          to just those cards.
        </Bullet>
        <Bullet>
          Make Ready also surfaces outside the module: the Asset Manager
          drawer shows a Make Ready block for the selected asset (its load
          list, when it was last checked, missing and expired counts, and a{' '}
          <strong>Run Make Ready check</strong> button — or the same assign
          flow if it is untracked; Fleet, Make Ready, Governance, Manager,
          and Supervisor team members can also change the list there), the
          asset record offers <strong>Assign load list</strong> when none is
          set, and the Vehicles list has a per-row Make Ready action that is
          disabled until the vehicle has a load list.
        </Bullet>
      </Bullets>
      <Screenshot url="fleet-equipment/make-ready-overview.png">
        The Make Ready Board with the fleet health ring, status chips, and a
        card per vehicle and asset.
      </Screenshot>
    </Section>

    <Section title="Running a check">
      <Bullets>
        <Bullet>
          Each required asset (e.g. a defibrillator) is marked Present,
          Missing, Removed, Replaced, or Assigned. Marking something
          Replaced opens a picker scoped to the same category and
          sub-category, showing whether the replacement is already in use
          elsewhere, so it gets recorded against the check. A slot defined
          with a category but no sub-category shows as "Any Medical Device"
          (for example) and the picker offers anything in that category.
          Assigned is for slots where no specific unit was ever recorded —
          the checker confirms the item is aboard and identifies which one
          it is.
        </Bullet>
        <Bullet>
          Each required consumable shows a present-count against what's
          expected, pre-filled from in-date stock already on file - reduce
          the count if something isn't physically there, or top it up.
        </Bullet>
        <Bullet>A pick list of everything short can be downloaded as a PDF.</Bullet>
      </Bullets>
    </Section>

    <Section title="Start of Shift">
      <p>
        <DocLink url="/start-of-shift" text="Start of Shift" /> (linked from
        the crew dashboard) combines the vehicle daily inspection (VDI) and
        the equipment Make Ready check into one flow, so crew only have to go
        through a single check at the start of a shift instead of two. The
        mobile app's Start of Shift has full parity with the web flow,
        including the editable inventory and accordion sections.
      </p>
    </Section>

    <Section title="Restock Queue">
      <Bullets>
        <Bullet>
          <DocLink url="/make-ready-restock-queue" text="Restock Queue" />{' '}
          (reached from the Restock Queue button in the Make Ready header)
          aggregates
          everything short across the whole fleet into one list, with a
          consolidated pick list PDF for restocking multiple vehicles/assets
          in one trip.
        </Bullet>
        <Bullet>
          <strong>Claim</strong> an item to show colleagues you are dealing
          with it — everyone else sees "Claimed by" against it, and you can
          release the claim if you get pulled away. Marking an item
          restocked records it as a Quick Top-up against that vehicle or
          asset, including the new stock's expiry date.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="History and trends">
      <Bullets>
        <Bullet>
          Every vehicle and asset page has a History section showing its past
          checks: date, who checked it, whether it was a full check or a
          quick top-up, and a summary of what was found (items needed, asset
          issues, or replacements).
        </Bullet>
        <Bullet>
          The <DocLink url="/make-ready-history" text="History" /> tab of
          Make Ready shows the same audit trail across the whole fleet,
          filterable by Full Check or Quick Top-up.
        </Bullet>
        <Bullet>
          Above the history table, two trend panels highlight the vehicles
          and assets whose checks most often find issues, and the items that
          run short most often across the fleet — useful for spotting
          recurring problems rather than one-offs.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Who can access what">
      <Info title="Permissions">
        Running a check is open to any signed-in crew member - it isn't
        restricted by team, since anyone may need to check a vehicle or asset
        during a shift. The Make Ready Board, Restock Queue, and
        History/Trends pages are restricted to the Fleet, Make Ready,
        Governance, Manager, or Supervisor teams. Assigning or changing a
        vehicle or asset's load list is open to those same teams (and site
        administrators). Creating and editing load lists — the Load Lists
        tab — is restricted further — see{' '}
        <DocLink url="/fleet-equipment/load-lists" text="Load Lists" docsPage />
        .
      </Info>
    </Section>

    <Section title="Maintenance">
      <p>
        Assets that require servicing show a maintenance log on their asset
        record, listing maintenance date, type, description, service
        provider, and expiry. New maintenance entries are recorded directly
        against the asset from{' '}
        <DocLink url="/asset-manager" text="Asset Manager" /> — see{' '}
        <DocLink
          url="/fleet-equipment/asset-manager"
          text="Asset Manager"
          docsPage
        />{' '}
        for who can record and view them.
      </p>
    </Section>
  </Page>
)

export default MakeReadyAndMaintenance
